import { useMutation, useQuery } from '@tanstack/react-query'
import Loading from 'components/Loading'
import Button from 'components/button'
import { SizableText, Text, View, XStack, YStack } from 'tamagui'
import { useTRPC } from 'utils/trpc'

const BillingActive = ({
    billingData,
}: {
    billingData: {
        hasBilling: true
        billingStatus?: string
        billingEmail?: string
        currentSeatCount: number
        maxSeats?: number | null
        currentPeriodEnd?: string
        cancelAtPeriodEnd?: boolean
        envelopeCount?: number | null
        envelopeCostCents?: number | null
        envelopeCurrency?: string | null
    }
}) => {
    const trpc = useTRPC()
    const getPortalMutation = useMutation(trpc.billing.getPortalUrl.mutationOptions())

    const statusColorMap: Record<string, string> = {
        active: '$primaryMuted',
        past_due: '$yellow5',
        canceled: '$red5',
        trialing: '$primaryMuted',
        unpaid: '$red5',
    }

    return (
        <View>
            <SizableText fontSize="$7" fontWeight="700" marginBottom="$4">
                Billing
            </SizableText>
            <YStack gap="$4" maxWidth={500}>
                <View
                    borderWidth={1}
                    borderColor="$borderColor"
                    borderRadius="$4"
                    padding="$4"
                >
                    <YStack gap="$2">
                        <XStack justifyContent="space-between" alignItems="center">
                            <Text>Status</Text>
                            <View
                                paddingHorizontal="$2"
                                paddingVertical="$1"
                                borderRadius="$3"
                                backgroundColor={statusColorMap[billingData.billingStatus ?? ''] ?? '$gray5'}
                            >
                                <SizableText fontSize="$2">
                                    {billingData.billingStatus ?? 'unknown'}
                                    {billingData.cancelAtPeriodEnd && ' (cancelling)'}
                                </SizableText>
                            </View>
                        </XStack>
                        <XStack justifyContent="space-between">
                            <Text>Seats</Text>
                            <Text fontWeight="bold">
                                {billingData.currentSeatCount} / {billingData.maxSeats ?? '—'}
                            </Text>
                        </XStack>
                        {billingData.envelopeCount != null && (
                            <>
                                <XStack justifyContent="space-between">
                                    <Text>Envelopes Sent</Text>
                                    <Text fontWeight="bold">{billingData.envelopeCount}</Text>
                                </XStack>
                                {billingData.envelopeCostCents != null && billingData.envelopeCostCents > 0 && billingData.envelopeCurrency && (
                                    <XStack justifyContent="space-between">
                                        <Text>Envelope Overage</Text>
                                        <Text fontWeight="bold">
                                            {new Intl.NumberFormat('en-AU', {
                                                style: 'currency',
                                                currency: billingData.envelopeCurrency.toUpperCase(),
                                            }).format(billingData.envelopeCostCents / 100)}
                                        </Text>
                                    </XStack>
                                )}
                            </>
                        )}
                        <XStack justifyContent="space-between">
                            <Text>Billing Email</Text>
                            <Text>{billingData.billingEmail}</Text>
                        </XStack>
                        {billingData.currentPeriodEnd && (
                            <XStack justifyContent="space-between">
                                <Text>Next Billing Date</Text>
                                <Text>{new Date(billingData.currentPeriodEnd).toLocaleDateString('en-AU')}</Text>
                            </XStack>
                        )}
                    </YStack>
                </View>
                <Button
                    variant="outlined"
                    onPress={async () => {
                        const { url } = await getPortalMutation.mutateAsync()
                        window.location.href = url
                    }}
                >
                    Manage Billing
                </Button>
            </YStack>
        </View>
    )
}

export default () => {
    const trpc = useTRPC()
    const billingQuery = useQuery(trpc.billing.getBilling.queryOptions())

    if (billingQuery.isLoading) return <Loading />

    const data = billingQuery.data
    if (!data || !data.hasBilling) return <Text>Billing is not set up for this organisation.</Text>
    return <BillingActive billingData={data} />
}
